import {
  CreateOrganizationRunEnvironmentRequestParams,
  DeleteOrganizationRunEnvironmentRequestParams,
  GetOrganizationRunEnvironmentListRequestParams,
  UpdateOrganizationRunEnvironmentRequestParams,
} from "common";
import { axios } from "@via/api/axios.tsx";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { type ProfileClusterForClient } from "common";

export const fetchOrganizationRunEnvironments = async (
  requestParams: GetOrganizationRunEnvironmentListRequestParams
): Promise<ProfileClusterForClient[]> => {
  try {
    const res = await axios.get(
      `/api/v1/organization/${requestParams.id}/run-environment`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationRunEnvironments = (
  requestParams: GetOrganizationRunEnvironmentListRequestParams
) => {
  return useQuery({
    queryKey: [
      `/api/v1/organization/${requestParams.id}/run-environment`,
      requestParams,
    ],
    queryFn: () => fetchOrganizationRunEnvironments(requestParams),
    retry: false,
  });
};

export const useCreateOrganizationRunEnvironment = (): UseMutationResult<
  void,
  ErrorNotification,
  {
    requestParams: CreateOrganizationRunEnvironmentRequestParams;
    requestBody: ProfileClusterForClient;
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      requestParams: CreateOrganizationRunEnvironmentRequestParams;
      requestBody: ProfileClusterForClient;
    }) => {
      await axios.post(
        `/api/v1/organization/${payload.requestParams.id}/run-environment`,
        {
          ...payload.requestBody,
        }
      );
    },
    onSuccess: (): void => {
      setNotification({
        id: "organization-run-env-successfully-created",
        message: "Organization Run Environment Successfully Created",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.includes("organization")
          );
        },
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-run-env-create-failed",
        message: "Run Organization Create Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateOrganizationRunEnvironment = (): UseMutationResult<
  void,
  ErrorNotification,
  {
    requestParams: UpdateOrganizationRunEnvironmentRequestParams;
    requestBody: ProfileClusterForClient;
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      requestParams: UpdateOrganizationRunEnvironmentRequestParams;
      requestBody: ProfileClusterForClient;
    }) => {
      await axios.patch(
        `/api/v1/organization/${payload.requestParams.id}/run-environment/${payload.requestParams.runEnvironmentId}`,
        {
          ...payload.requestBody,
        }
      );
    },
    onSuccess: (): void => {
      setNotification({
        id: "organization-run-env-successfully-updated",
        message: "Organization Run Environment Successfully Updated",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.includes("organization")
          );
        },
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-run-env-update-failed",
        message: "Run Organization Update Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteOrganizationRunEnvironment = (): UseMutationResult<
  void,
  ErrorNotification,
  DeleteOrganizationRunEnvironmentRequestParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: DeleteOrganizationRunEnvironmentRequestParams
    ) => {
      await axios.delete(
        `/api/v1/organization/${payload.id}/run-environment/${payload.runEnvironmentId}`
      );
    },
    onSuccess: (): void => {
      setNotification({
        id: "organization-run-env-successfully-deleted",
        message: "Organization Run Environment Successfully Deleted",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.refetchQueries({
        predicate: query => {
          const queryKeyString = query.queryKey[0];
          return (
            typeof queryKeyString === "string" &&
            queryKeyString.includes("organization")
          );
        },
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "organization-run-env-delete-failed",
        message: "Run Organization Deletion Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
